import { useEffect, useState, useContext } from "react";
import { IconButton, Row, LoadingSkeleton } from "@narmi/design_system";
import { DateTime } from "luxon";
import { formatShortCurrency } from "../../components/currency";
import { Summary, SummaryRow } from "./Cards";
import { PrimacyScoreCard } from "./PrimacyScoreCard";
import { ThreadStatistics, TransactionStatistics, UserStatistics } from "./Dashboard";
import { handleErrorResponse } from "@utils/api";
import NotificationContext from "cerulean/src/NotificationContext";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
const numberFormatter = new Intl.NumberFormat("en-US");

const UserCards = ({ data, date }: { data: UserStatistics; date: string }) => {
  return (
    <SummaryRow>
      <Summary
        title="Total users"
        value={numberFormatter.format(data.total)}
        change={Number(data.total_change)}
        changeReferenceDate={date}
      />
      <Summary
        title="Enrolled users"
        value={numberFormatter.format(data.enrolled)}
        change={Number(data.enrolled_change)}
        changeReferenceDate={date}
      />
      <Summary
        title="Active users"
        value={numberFormatter.format(data.active)}
        change={Number(data.active_change)}
        changeReferenceDate={date}
        showPercentChange={false}
      />
    </SummaryRow>
  );
};

const ThreadCards = ({ data }: { data: ThreadStatistics }) => {
  return (
    <SummaryRow>
      <Summary
        title="Total threads"
        value={numberFormatter.format(data.total)}
        change={Number(data.total_change)}
        showPercentChange={false}
      />
      <Summary
        title="Open threads"
        value={numberFormatter.format(data.open)}
        change={Number(data.open_change)}
        showPercentChange={false}
      />
      <Summary
        title="Closed threads"
        value={numberFormatter.format(data.closed)}
        change={Number(data.closed_change)}
        showPercentChange={false}
      />
      <Summary
        title="Your replies"
        value={numberFormatter.format(data.replies)}
        change={Number(data.replies_change)}
        showPercentChange={false}
      />
    </SummaryRow>
  );
};

const TransactionCards = ({ date }: { date: string }) => {
  const { sendNotification } = useContext(NotificationContext);
  const [data, setData] = useState<TransactionStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch("/api/dashboard/transaction_statistics/");
        if (!response.ok) {
          const errNotification = await handleErrorResponse(response);
          sendNotification(errNotification);
        }

        // We have never calculated transaction statistics ever before, and the task to
        // calculate them has been queued on the backend. This should only happen once.
        if (response.status === 202) {
          return;
        }

        const transactionData = await response.json();
        setData(transactionData);
      } catch (error) {
        sendNotification({
          type: "negative",
          text: "Failed to fetch transaction statistics",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (!data && !isLoading) {
    return null;
  }

  return (
    <LoadingSkeleton isLoading={isLoading}>
      {data ? (
        <SummaryRow>
          <Summary
            title="Total incoming transactions"
            value={numberFormatter.format(data.total)}
            change={Number(data.total_change)}
            changeReferenceDate={date}
          />
          <Summary
            title="Total deposit amount"
            value={formatShortCurrency(data.sum)}
            change={Number(data.sum_change)}
            changeReferenceDate={date}
          />
          <Summary
            title="Average deposit amount"
            value={formatShortCurrency(data.average)}
            change={Number(data.average_change)}
            changeReferenceDate={date}
          />
        </SummaryRow>
      ) : null}
    </LoadingSkeleton>
  );
};

export const DigitalBanking = ({
  userData,
  threadData,
}: {
  userData: UserStatistics;
  threadData: ThreadStatistics;
}) => {
  const { isFeatureEnabled } = useUserConfiguration();

  const date = DateTime.now().minus({ days: 30 }).toFormat("MMM dd, yyyy");

  const csvUrl = () => {
    const userDataOrDefault = userData ?? {};
    const threadDataOrDefault = threadData ?? {};
    const titles = Object.keys(userDataOrDefault).concat(Object.keys(threadDataOrDefault));
    const data = Object.values(userDataOrDefault)
      .concat(Object.values(threadDataOrDefault))
      // prevents null values from being empty strings in csv
      .map((value) => {
        if (value === null) {
          return 0;
        }
        return value;
      });
    const csv = [titles, data].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    return URL.createObjectURL(blob);
  };

  return (
    <>
      <div className="margin--bottom--l">
        <Row gapSize="xs" alignItems="center">
          <Row.Item shrink>
            <h2>Digital Banking</h2>
          </Row.Item>
          <Row.Item>
            <IconButton
              name="download"
              textSize="l"
              as="a"
              href={csvUrl()}
              download={`digital_banking_metrics_${DateTime.now().toFormat("MM/dd/yyyy")}.csv`}
            />
          </Row.Item>
        </Row>
        <TransactionCards date={date} />
        <UserCards data={userData} date={date} />
        {isFeatureEnabled("primacy_score") && (
          <div className="margin--top--m">
            <PrimacyScoreCard />
          </div>
        )}
      </div>
      <div className="margin--bottom--l">
        <h2>Messages</h2>
        <ThreadCards data={threadData} />
      </div>
    </>
  );
};
